import { KRONOS_PREFS_SCHEMA_VERSION, userPrefsStorageKey } from './constants';
import { readJsonLocal, writeJsonLocal } from './jsonLocal';

export function emptyUserPrefsV1() {
    return {
        __meta: { schemaVersion: KRONOS_PREFS_SCHEMA_VERSION },
        analysesStatsDateUi: null,
        /** Selected cinema / hall filters on stats + attendance analysis. */
        analysesStatsVenueUi: null,
        /** FiltersCard expanded on stats / analysis screens. */
        analysesStatsFiltersExpanded: null,
        /** FiltersCard expanded on generic list screens (customers, tickets, …). */
        listFiltersExpanded: null,
        commandPaletteUsed: null,
        kUserSettings: null,
        /** @type {Record<string, Array<{ id: string, desc: boolean }>>|null} */
        listTablesSortState: null,
    };
}

/** @returns {object|null} Whole stored JSON — same shape as future API `users.config`. */
export function readUserPrefsDoc(userId) {
    const key = userPrefsStorageKey(userId);
    const v = readJsonLocal(key);
    if (!v || typeof v !== 'object') {
        return null;
    }
    return v;
}

export function writeUserPrefsDoc(userId, doc) {
    const key = userPrefsStorageKey(userId);
    const next = {
        ...emptyUserPrefsV1(),
        ...doc,
        __meta: {
            ...emptyUserPrefsV1().__meta,
            ...(doc.__meta || {}),
            schemaVersion: KRONOS_PREFS_SCHEMA_VERSION,
        },
    };
    writeJsonLocal(key, next);
}

/**
 * @param {string|null} userId
 * @param {string} dotPath
 * @returns {string|null}
 */
export function readUserPrefString(userId, dotPath) {
    if (!userId) {
        return null;
    }
    const doc = readUserPrefsDoc(userId) || emptyUserPrefsV1();
    const parts = String(dotPath).split('.').filter(Boolean);
    let o = doc;
    for (let i = 0; i < parts.length - 1; i++) {
        o = o[parts[i]];
        if (o == null || typeof o !== 'object') {
            return null;
        }
    }
    const v = o[parts[parts.length - 1]];
    if (v == null) {
        return null;
    }
    return typeof v === 'string' ? v : String(v);
}

export function writeUserPrefString(userId, dotPath, value) {
    if (!userId) {
        return;
    }
    const doc = readUserPrefsDoc(userId) || emptyUserPrefsV1();
    const parts = String(dotPath).split('.').filter(Boolean);
    let o = doc;
    for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (!o[p] || typeof o[p] !== 'object') {
            o[p] = {};
        }
        o = o[p];
    }
    o[parts[parts.length - 1]] = value;
    writeUserPrefsDoc(userId, doc);
}

export function mergeUserPrefsPatch(userId, patch) {
    if (!userId) {
        return null;
    }
    const cur = readUserPrefsDoc(userId) || emptyUserPrefsV1();
    const next = {
        ...cur,
        ...patch,
        __meta: { ...cur.__meta, ...(patch.__meta || {}), schemaVersion: KRONOS_PREFS_SCHEMA_VERSION },
    };
    if (Object.prototype.hasOwnProperty.call(patch, 'analysesStatsDateUi')) {
        next.analysesStatsDateUi = patch.analysesStatsDateUi;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'analysesStatsVenueUi')) {
        next.analysesStatsVenueUi = patch.analysesStatsVenueUi;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'analysesStatsFiltersExpanded')) {
        next.analysesStatsFiltersExpanded = patch.analysesStatsFiltersExpanded;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'listFiltersExpanded')) {
        next.listFiltersExpanded = patch.listFiltersExpanded;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'kUserSettings')) {
        next.kUserSettings = patch.kUserSettings;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'listTablesSortState')) {
        next.listTablesSortState = patch.listTablesSortState;
    }
    writeUserPrefsDoc(userId, next);
    return next;
}
